import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Form, Input, Select, Space, Typography } from "antd";
import { SectionPanelProps } from "./SectionTypes";
import { useJsonSection } from "./useJsonSection";
import { ClientConfig } from "../../../model/client/ClientSettings";
import { CopyFromClientButton } from "./CopyFromClientModal";

const { Text, Paragraph } = Typography;

type FormKey = 'health' | 'newMember' | 'signup';

const FORM_OPTIONS: { value: FormKey; label: string; settingKey: string }[] = [
    { value: 'health', label: 'Health questionnaire (PARQ)', settingKey: 'forms.health' },
    { value: 'newMember', label: 'New member form', settingKey: 'forms.newMember' },
    { value: 'signup', label: 'Guest signup form', settingKey: 'forms.signup' },
];

interface FormsValue {
    health: string;
    newMember: string;
    signup: string;
}

// Settings panel for the configurable questionnaires (forms.health/newMember/
// signup). MVP is a raw-JSON editor with client-side validation — the schema is
// the QuestionnaireSchema shape rendered by DynamicQuestionnaire. A visual
// field-by-field editor can layer on later without changing the stored shape.
export const FormsSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const extract = useCallback((config: ClientConfig | undefined): FormsValue => {
        const forms = config?.settings.forms;
        return {
            health: prettify(forms?.healthJson),
            newMember: prettify(forms?.newMemberJson),
            signup: prettify(forms?.signupJson),
        };
    }, []);
    const { clientId, value, setValue, initial, loadError, saveError, saving, save, reload } =
        useJsonSection<FormsValue>(extract);
    const [selected, setSelected] = useState<FormKey>('health');

    const jsonError = useMemo(() => {
        if (value === null) return null;
        try {
            JSON.parse(value[selected]);
            return null;
        } catch (err) {
            return err instanceof Error ? err.message : 'Invalid JSON';
        }
    }, [value, selected]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-forms-load-error" />;
    }
    if (value === null || initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    const option = FORM_OPTIONS.find((o) => o.value === selected)!;
    const dirty = value[selected] !== initial[selected];
    const handleSave = () => {
        // Persist a compact form of the validated JSON for the selected form only.
        const compact = JSON.stringify(JSON.parse(value[selected]));
        save([{ key: option.settingKey, value: compact, valueType: 'json' }], { ...value, [selected]: compact });
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <CopyFromClientButton targetClientId={clientId} groups={['forms']} onCopied={reload} />
            <Form layout="vertical" data-testid="settings-forms-form">
                <Form.Item label="Form">
                    <Select
                        data-testid="settings-forms-select"
                        style={{ width: 320 }}
                        value={selected}
                        onChange={setSelected}
                        options={FORM_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    />
                </Form.Item>
                <Paragraph type="secondary">
                    Edit the questionnaire schema as JSON: ordered <Text code>sections</Text>, each with
                    <Text code>fields</Text> ({'{ key, label, type, required, options, flagWhen }'}). Field types:
                    radio, checkboxGroup, text, textarea, date, select, boolean, info.
                </Paragraph>
                <Form.Item
                    label="Schema JSON"
                    validateStatus={jsonError ? 'error' : undefined}
                    help={jsonError ?? undefined}
                >
                    <Input.TextArea
                        data-testid="settings-forms-json"
                        rows={18}
                        value={value[selected]}
                        onChange={(e) => setValue({ ...value, [selected]: e.target.value })}
                        style={{ fontFamily: 'monospace' }}
                    />
                </Form.Item>
            </Form>
            {saveError && <Alert type="error" message={saveError} data-testid="settings-forms-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-forms-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || jsonError !== null}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-forms-discard-btn"
                    onClick={() => setValue({ ...value, [selected]: initial[selected] })}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};

// Pretty-print stored JSON for editing; leave non-JSON untouched so the editor
// can still surface and fix a malformed value rather than blanking it.
function prettify(json: string | undefined): string {
    if (!json) return '{}';
    try {
        return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
        return json;
    }
}
